import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { useMap } from "react-leaflet";
import { DateTime } from "luxon";

import { usePromiseStatus } from "../../../Lib/UI/Hooks/usePromiseStatus";
import { useInstantNotifier } from "../../../Lib/Notifier/useInstantNotifier";
import { ITileCacheSeeder, ITileCacheSeedOperation, TileCacheSeedOperationChangeEvent, TileCacheSeedOperationStartedEvent, TileCacheSeedOperationStatus } from "../../MapComponents/TileCache/types";
import { IMapLayerSnapshotGenerator } from "../../MapComponents/snapshotGenerator";


export interface DownloadMapDataListProps {
    tileCacheSeeder: ITileCacheSeeder;
    urlTemplate: string;
    urlSubdomains?: string[] | null;
    maxZoom: number;
    minZoom?: number;
    snapshotGen?: IMapLayerSnapshotGenerator;
}

const useDownloadMapDataList = (props: DownloadMapDataListProps) => {

    const notifier = useInstantNotifier();
    const map = useMap();

    const maxZoom = props.maxZoom;
    const minZoom = props.minZoom ?? 4;
    const currentZoom = map.getZoom();
    const latLngBounds = map.getBounds();
    const crs = map.options.crs ?? L.CRS.EPSG3857;

    //Interface
    const [historyItems, setHistoryItems] = useState<ITileCacheSeedOperation[] | null>(null);
    const historyItemsRef = useRef<ITileCacheSeedOperation[] | null>(null);
    const setHistoryItemsInternal = (value: ITileCacheSeedOperation[]) => {
        historyItemsRef.current = value;
        setHistoryItems(historyItemsRef.current);
    };

    const [currentThumbUrl, setCurrentThumbUrl] = useState<string | null>(null);
    const listStatus = usePromiseStatus<ITileCacheSeedOperation[]>();

    const startDownloadStatus = usePromiseStatus<ITileCacheSeedOperation>();

    const numActiveDownloads = historyItems ? historyItems.filter(op => op.status === TileCacheSeedOperationStatus.InProgress).length : 0;
    const zoomIsOutOfRange = minZoom > currentZoom;
    const isAtConcurrencyLimit = numActiveDownloads >= props.tileCacheSeeder.maxInProgressDownloads();
    const downloadAvailable = !zoomIsOutOfRange && !isAtConcurrencyLimit;


    const loadHistory = () => {

        const currentDate = DateTime.now();

        const promise = new Promise<ITileCacheSeedOperation[]>((resolve, reject) => {

            props.tileCacheSeeder.cullSeedHistory(currentDate)
                .then(() => {
                    props.tileCacheSeeder.getSeedHistory().then(resolve).catch(reject);
                })
                .catch(reject);

        });

        listStatus.setPromise(promise);

        promise.then(setHistoryItemsInternal);
    };

    const startDownload = async () => {
        const downloadPromise =  props.tileCacheSeeder.seedFromBounds(crs, latLngBounds, maxZoom, currentZoom, props.urlTemplate, props.urlSubdomains, currentThumbUrl)
        downloadPromise.catch(error => notifier.error("Failed to start download", error));
        startDownloadStatus.setPromise(downloadPromise)
        await downloadPromise;
    };


    //Internals
    const generateThumb = () => {

        if (!props.snapshotGen)
            return;

        props.snapshotGen.generate(300, null)
            .then(setCurrentThumbUrl);

    };

    const handleOpStarting = (event: TileCacheSeedOperationStartedEvent) => {

        if (!historyItemsRef.current)
            return;

        setHistoryItemsInternal([event.op, ...historyItemsRef.current]);
    };

    const handleOpStatusChange = (event: TileCacheSeedOperationChangeEvent) => {

        if (!historyItemsRef.current)
            return;

        setHistoryItemsInternal(
            historyItemsRef.current.map(item => {
                if (event.opId === item.id) {
                    return { 
                        ...item, 
                        status: !!event.status ? event.status : item.status, 
                        downloadedBytes: !!event.downloadedBytes ? event.downloadedBytes : item.downloadedBytes, 
                        downloadedTotal: !!event.downloadedTotal ? event.downloadedTotal : item.downloadedTotal, 
                        error: !!event.error ? event.error : item.error
                    }
                }
                return item;
            })
        );
    };

    const handleOpRemoved = (event: TileCacheSeedOperationChangeEvent) => {

        if (!historyItemsRef.current)
            return;

        setHistoryItemsInternal(historyItemsRef.current.filter(hi => hi.id !== event.opId) ?? []);
    };

    useEffect(() => {
        loadHistory();
        generateThumb();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        props.tileCacheSeeder.addListener('opstarting', handleOpStarting);
        props.tileCacheSeeder.addListener('opstatuschange', handleOpStatusChange);
        props.tileCacheSeeder.addListener('opprogresschange', handleOpStatusChange);
        props.tileCacheSeeder.addListener('opremoved', handleOpRemoved);

        return () => {
            props.tileCacheSeeder.removeListener('opstarting', handleOpStarting);
            props.tileCacheSeeder.removeListener('opstatuschange', handleOpStatusChange);
            props.tileCacheSeeder.removeListener('opprogresschange', handleOpStatusChange);
            props.tileCacheSeeder.removeListener('opremoved', handleOpRemoved);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.tileCacheSeeder, historyItems]);

    return {
        loadHistory, listStatus,
        historyItems,
        currentThumbUrl, 
        zoomIsOutOfRange, isAtConcurrencyLimit,
        downloadAvailable, startDownload, startDownloadStatus,
        numActiveDownloads,
    };

};

export default useDownloadMapDataList;